import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  Backdrop,
  ModalContainer,
  CloseButton,
  ModalContent,
  Badge,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  FeaturesList,
  FeatureItem,
  ModalFooter,
  GotItButton,
} from './StyledComponents';

/**
 * ComingSoonModal - A reusable modal for displaying "Coming Soon" features
 *
 * Usage:
 * <ComingSoonModal
 *   title="Customer Messaging"
 *   description="We're building a powerful messaging system."
 *   features={[
 *     "Message customers directly",
 *     "View conversation history",
 *     "Get notifications"
 *   ]}
 * >
 *   <ButtonV2>Message Customer</ButtonV2>
 * </ComingSoonModal>
 */
const ComingSoonModal = ({ title, description, features, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop itself, not the modal
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Clone the child element and add onClick handler
  const trigger = React.cloneElement(children, {
    onClick: handleOpen,
  });

  const modal = isOpen
    ? ReactDOM.createPortal(
        <EnhancedThemeProvider>
          <Backdrop onClick={handleBackdropClick}>
            <ModalContainer
              role="dialog"
              aria-labelledby="modal-title"
              aria-modal="true"
            >
              <CloseButton onClick={handleClose} aria-label="Close modal">
                <svg
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
                </svg>
              </CloseButton>

              <ModalContent>
                <ModalHeader>
                  <Badge>Coming Soon</Badge>
                  <ModalTitle id="modal-title">{title}</ModalTitle>
                  {description && (
                    <ModalDescription>{description}</ModalDescription>
                  )}
                </ModalHeader>

                {features && features.length > 0 && (
                  <FeaturesList>
                    {features.map((feature, index) => (
                      <FeatureItem key={index}>{feature}</FeatureItem>
                    ))}
                  </FeaturesList>
                )}
              </ModalContent>

              <ModalFooter>
                <GotItButton onClick={handleClose}>Got It</GotItButton>
              </ModalFooter>
            </ModalContainer>
          </Backdrop>
        </EnhancedThemeProvider>,
        document.body
      )
    : null;

  return (
    <>
      {trigger}
      {modal}
    </>
  );
};

ComingSoonModal.propTypes = {
  /** Feature title (e.g., "Customer Messaging") */
  title: PropTypes.string.isRequired,
  /** Optional description text */
  description: PropTypes.string,
  /** Array of feature strings to display as bullet list */
  features: PropTypes.arrayOf(PropTypes.string),
  /** Trigger element (button/link) that opens the modal */
  children: PropTypes.element.isRequired,
};

ComingSoonModal.defaultProps = {
  description: null,
  features: [],
};

export default ComingSoonModal;
export { default as GenericComingSoon } from './GenericComingSoon';
